import React from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  selectAuth,
  selectCurrentConference,
  selectCurrentRole,
  selectCurrentTrack,
  selectImpersonatedTrackId,
  selectIsEmailVerified,
  selectIsLoading,
  selectIsPersonalInfoCompleted,
  selectIsRequiredInfoCompleted,
} from '../store/selectors';
import { AppState } from '../store/state';
import { getRouteByName, RouteDefinition, RouteName } from './routes';
import Loading from '../components/Loading/Loading';
import { AppDispatch } from '../store/store';
import { generatePath } from 'react-router';
import { Conference, Role, Track } from '../store/conference/types';
import { can } from '../helpers/permissions';
import { OpenSnackBarDTO } from '../store/error/types';
import errorSlice from '../store/error/slice';
import { useTranslation } from 'react-i18next';

interface RedirectOption {
  routeName: RouteName;
  params?: object;
}
const redirectTo = (dest: RedirectOption) => {
  const path = generatePath(getRouteByName(dest.routeName).path, dest.params);
  return <Redirect to={path} />;
};

interface RedirectorProps {
  isLoading: boolean;
  isLoggedIn: boolean;
  isImpersonated: boolean;
  isEmailVerified: boolean;
  isPersonalInfoCompleted: boolean;
  isRequiredInfoCompleted: boolean;
  currentTrack: Track | null;
  currentRole: Role | null;
  currentConference: Conference | null;
  routeToAccess: RouteDefinition;
  children?: React.ReactNode;
  openSnackBarAction: (data: OpenSnackBarDTO) => void;
}

const Redirector: React.FC<RedirectorProps> = ({
  isLoading,
  isLoggedIn,
  isImpersonated,
  isEmailVerified,
  isPersonalInfoCompleted,
  isRequiredInfoCompleted,
  currentTrack,
  currentRole,
  currentConference,
  routeToAccess,
  children,
  openSnackBarAction,
}) => {
  const { t } = useTranslation();

  const setRedirectOption = (r: RedirectOption | 'NO_REDIRECT') => {
    redirection = r;
  };

  let redirection: RedirectOption | 'NO_REDIRECT' = 'NO_REDIRECT';

  /*
   ****** helps debug ******* /

  let str = isLoading ? '-Loading ' : '-Not Loading ';
  if (routeToAccess === undefined) {
    str = str.concat('-Undefined route ');
  } else {
    str = str.concat(routeToAccess.logged_access ? '-Logged access ' : '-Not logged acess ');
    str = str.concat(routeToAccess.anonymous_access ? '-Anonymous access ' : '-Not anonymous acess ');
    str = str.concat('-' + routeToAccess.path + ' ');
    str = str.concat(!!auth.loggedIn ? '-Logged In ' : '-Not logged in ');
  }
  console. log(str); */

  if (isLoading) {
    return <Loading />;
  }

  if (routeToAccess === undefined) {
    setRedirectOption({ routeName: 'RouteLogin' });
  } else if (routeToAccess.name === 'RouteRoot') {
    // setRedirectOption({ routeName: 'RouteLogin' });
  } else if (['RouteSendEmailVerificationPage'].includes(routeToAccess.name) && isEmailVerified) {
    setRedirectOption({ routeName: 'RouteConferences' });
  } else if (routeToAccess.anonymous_access && routeToAccess.logged_access) {
    setRedirectOption('NO_REDIRECT');
  } else if (routeToAccess.logged_access && !isLoggedIn) {
    if (routeToAccess.name != 'RouteLogout') {
      localStorage.setItem('next', window.location.href);
      openSnackBarAction({
        message: t('You tried to access a private page. First, you need to log in or sign up. Thank you!'),
        severity: 'warning',
      });
    }
    setRedirectOption({ routeName: 'RouteLogin' });
  } else if (!routeToAccess.logged_access && isLoggedIn) {
    setRedirectOption({ routeName: 'RouteConferences' });
  } else if (routeToAccess.email_verified_access && !isEmailVerified) {
    setRedirectOption({ routeName: 'RouteSendEmailVerificationPage' });
  } else if (!isImpersonated && routeToAccess.personal_info_filled_access && !isPersonalInfoCompleted) {
    setRedirectOption({ routeName: 'RoutePersonalInfoWizard' });
  } else if (!isImpersonated && routeToAccess.name == 'RoutePersonalInfoWizard' && isPersonalInfoCompleted) {
    if (currentConference && currentTrack) {
      setRedirectOption({
        routeName: 'RouteConferenceHome',
        params: { conference: currentConference.slug, track: currentTrack.slug },
      });
    } else {
      setRedirectOption({ routeName: 'RouteConferences' });
    }
  } else if (routeToAccess.track_selected_access && !currentTrack) {
    setRedirectOption({ routeName: 'RouteConferences' });
  } else if (
    routeToAccess.track_selected_access &&
    currentTrack &&
    !currentTrack.wizard_completed &&
    routeToAccess.name !== 'RouteConferenceWizard'
  ) {
    if (currentRole?.type == 'chair') {
      setRedirectOption({
        routeName: 'RouteConferenceWizard',
        params: { conference: currentConference?.slug, track: currentTrack?.slug },
      });
    } else {
      setRedirectOption({ routeName: 'RouteConferences' });
    }
  } else if (
    routeToAccess.track_selected_access &&
    currentTrack &&
    currentTrack.wizard_completed &&
    routeToAccess.name == 'RouteConferenceWizard'
  ) {
    setRedirectOption({
      routeName: 'RouteConferenceHome',
      params: { conference: currentConference?.slug, track: currentTrack?.slug },
    });
  } else if (
    !isImpersonated &&
    routeToAccess.track_selected_access &&
    !isRequiredInfoCompleted &&
    routeToAccess.name !== 'RouteUserProfileWizard'
  ) {
    setRedirectOption({ routeName: 'RouteUserProfileWizard' });
  } else if (routeToAccess.permission && !can(routeToAccess.permission)) {
    if (routeToAccess.track_selected_access) {
      setRedirectOption({
        routeName: 'RouteConferenceHome',
        params: { conference: currentConference?.slug, track: currentTrack?.slug },
      });
    } else {
      setRedirectOption({ routeName: 'RouteConferences' });
    }
  } else if (!routeToAccess.impersonated_access && isImpersonated) {
    setRedirectOption({
      routeName: 'RouteConferenceHome',
      params: { conference: currentConference?.slug, track: currentTrack?.slug },
    });
  } else {
    setRedirectOption('NO_REDIRECT'); // This means render the component within fragment
  }

  // @ts-ignore
  //console.log('Redirect to: ' + (typeof redirection === 'object' ? redirection.routeName : redirection));

  return (
    <React.Fragment>
      {redirection !== 'NO_REDIRECT' && redirectTo(redirection)}
      {redirection === 'NO_REDIRECT' && children}
    </React.Fragment>
  );
};

const mapStateToProps = (state: AppState) => ({
  isLoading: selectIsLoading(state),
  isLoggedIn: selectAuth(state).loggedIn,
  isImpersonated: !!selectImpersonatedTrackId(state),
  isEmailVerified: selectIsEmailVerified(state),
  isPersonalInfoCompleted: selectIsPersonalInfoCompleted(state),
  isRequiredInfoCompleted: selectIsRequiredInfoCompleted(state),
  currentTrack: selectCurrentTrack(state),
  currentRole: selectCurrentRole(state),
  currentConference: selectCurrentConference(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  openSnackBarAction: (data: OpenSnackBarDTO) => dispatch(errorSlice.actions['OPEN:SNACKBAR'](data)),
});
export default connect(mapStateToProps, mapDispatchToProps)(Redirector);
